import { ref, computed } from 'vue'
import { jsonapiClient } from '@/api/client'
import type { paths } from '@/api/schema'
import { compactPostalCode, isCompletePostalCode, blankAddressDraft, type AddressDraft, type House } from '@/lib/address'

/**
 * « Which house is this? » — the address form's STATE MACHINE (design 170;
 * the table in doc/FEATURE_CUSTOMER_DEDUP.md is the design of record).
 *
 * The office types a civic number and a postal code; the registry answers ONE
 * house, a short list, « add the street », or nothing. A typed street switches
 * the lookup to street mode (the registry's rungs, then the map). The place
 * question — « do we already have an address here? » — rides on a found house
 * for intake forms only.
 *
 * ⭐ The gate is on the SAVE, not on a button: `ensureAnswered()` runs the
 * lookup for the current text when it has not answered yet, then the host reads
 * `blocksSave`. Only `failed` (our geocoder down) lets an address through
 * unanswered; the async job asks again.
 *
 * Signatures, per mode: pair = number + postal; street = number + postal +
 * street + city. An edit that changes the signature drops the answer at once
 * (`touch`), so a found line never sits under new text.
 */

type GeocodeMeta =
  paths['/api/service_locations/geocode']['post']['responses']['200']['content']['application/vnd.api+json']['meta']

export type PlaceCandidate = GeocodeMeta['candidates'][number]
// The draft and the house live in lib/address with the rest of the grammar;
// re-exported here for the hosts that only know the lookup.
export type { AddressDraft, House }
export { blankAddressDraft }

export type LookupState =
  | 'idle'
  | 'geocoding'
  | 'found'
  | 'choose'
  | 'ask_street'
  | 'candidate'
  | 'not_found'
  | 'unlocated'
  | 'failed'

export type PlaceAnswer = { use: 'existing'; candidate: PlaceCandidate } | { use: 'new' }

interface Trimmed {
  civicNumber: string
  postalCode: string
  street: string
  city: string
}

const trimmed = (draft: AddressDraft): Trimmed => ({
  civicNumber: draft.civicNumber.trim(),
  postalCode: compactPostalCode(draft.postalCode),
  street: draft.street.trim(),
  city: draft.city.trim(),
})

// Street mode is implied by a street being typed.
const signatureOf = (draft: AddressDraft): string => {
  const t = trimmed(draft)
  return t.street
    ? ['s', t.civicNumber, t.postalCode, t.street, t.city].join('|')
    : ['p', t.civicNumber, t.postalCode].join('|')
}

// Enough to ask honestly: a number, and a full postal code or a street with a
// town. Less than that pins the wrong street in the wrong town.
const askable = (draft: AddressDraft): boolean => {
  const t = trimmed(draft)
  if (!t.civicNumber) return false
  if (t.street) return !!t.city || isCompletePostalCode(t.postalCode)
  return isCompletePostalCode(t.postalCode)
}

// The states in which the save waits: a request in flight or a question open.
const STOPPING: ReadonlySet<LookupState> = new Set(['geocoding', 'choose', 'ask_street', 'candidate', 'not_found'])

export function useAddressLookup(
  read: () => AddressDraft | undefined,
  options: {
    // Intake asks « do we already have an address here? » on a found house;
    // the edit forms do not (they would find themselves).
    placeQuestion?: boolean
    // The location being edited, left out of the place candidates server-side.
    excludeId?: () => string | null
  } = {},
) {
  const placeQuestion = options.placeQuestion ?? true

  const state = ref<LookupState>('idle')
  // An edit form opened on a stored address: it owes no new answer, and the
  // row may fold it to a pill until it changes.
  const settled = ref(false)
  const houses = ref<House[]>([])
  const house = ref<House | null>(null)
  const placeCandidates = ref<PlaceCandidate[]>([])
  const placeAnswer = ref<PlaceAnswer | null>(null)

  let checkedSignature = ''
  let pending: Promise<void> | null = null
  let postalTimer: ReturnType<typeof setTimeout> | undefined
  const POSTAL_INPUT_DEBOUNCE_MS = 250

  const wipe = () => {
    houses.value = []
    house.value = null
    placeCandidates.value = []
    placeAnswer.value = null
  }

  const post = (attributes: { civic_number: string; postal_code: string | null; street: string | null; city: string | null }) =>
    jsonapiClient.post('/api/service_locations/geocode', {
      data: {
        type: 'service_location',
        attributes: { ...attributes, exclude_id: options.excludeId?.() ?? null },
      },
    })

  const settleOn = (found: House, candidates: PlaceCandidate[]) => {
    house.value = found
    placeCandidates.value = placeQuestion ? candidates : []
    state.value = placeCandidates.value.length > 0 ? 'candidate' : 'found'
  }

  const apply = (meta: GeocodeMeta) => {
    houses.value = meta.houses
    const first = meta.houses[0]
    switch (meta.status) {
      case 'ok':
        if (first) settleOn(first, meta.candidates)
        else state.value = 'failed'
        return
      case 'choose':
        state.value = 'choose'
        return
      case 'ask_street':
        state.value = 'ask_street'
        return
      case 'not_found':
        state.value = 'not_found'
        return
      default:
        state.value = 'failed'
    }
  }

  const run = async (t: Trimmed, signature: string) => {
    try {
      const response = await post({
        civic_number: t.civicNumber,
        postal_code: t.postalCode || null,
        street: t.street || null,
        city: t.city || null,
      })
      // Stale response: the text changed while this was in flight — the newer
      // check owns the state now.
      if (signature !== checkedSignature) return
      apply(response.data.meta)
    } catch {
      // The endpoint itself already fails soft; this catches the network to it.
      if (signature !== checkedSignature) return
      state.value = 'failed'
    }
  }

  // An edit that changes the signature returns to idle at once; the next check
  // (blur, a complete postal code, or the save) asks again.
  const touch = () => {
    const draft = read()
    if (!draft) return
    if (signatureOf(draft) === checkedSignature) return
    checkedSignature = ''
    settled.value = false
    if (state.value !== 'idle') {
      state.value = 'idle'
      wipe()
    }
  }

  // Asks when the current text has not been asked about; returns the request in
  // flight for that text when it has, so a save never starts a second one.
  const check = (): Promise<void> => {
    const draft = read()
    if (!draft) return Promise.resolve()
    const signature = signatureOf(draft)
    if (signature === checkedSignature) return pending ?? Promise.resolve()
    checkedSignature = signature
    settled.value = false
    wipe()
    if (!askable(draft)) {
      state.value = 'idle'
      pending = null
      return Promise.resolve()
    }
    state.value = 'geocoding'
    const request = run(trimmed(draft), signature).finally(() => {
      if (pending === request) pending = null
    })
    pending = request
    return request
  }

  // The postal code fires the lookup the moment it is complete, the way the
  // cell-phone dedup fires when the number is — no blur needed.
  const onPostalInput = () => {
    touch()
    clearTimeout(postalTimer)
    const draft = read()
    if (!draft || !isCompletePostalCode(draft.postalCode)) return
    postalTimer = setTimeout(() => {
      void check()
    }, POSTAL_INPUT_DEBOUNCE_MS)
  }

  // The save's gate: ask for the current text if nobody has, and wait.
  const ensureAnswered = async () => {
    clearTimeout(postalTimer)
    await check()
  }

  // « choose » → the picked street. Asked back in street mode so the place
  // question can be put for THAT house (the server answers candidates only on
  // one house); the house in hand stands if the server will not say more.
  const pickHouse = (picked: House): Promise<void> => {
    if (state.value !== 'choose') return Promise.resolve()
    const draft = read()
    const signature = checkedSignature
    house.value = picked
    state.value = 'geocoding'
    const request = (async () => {
      try {
        const response = await post({
          civic_number: draft?.civicNumber.trim() ?? '',
          postal_code: picked.postal_code,
          street: picked.street_label,
          city: picked.city,
        })
        if (signature !== checkedSignature) return
        const meta = response.data.meta
        const confirmed = meta.houses[0]
        if (meta.status === 'ok' && confirmed) settleOn(confirmed, meta.candidates)
        else settleOn(picked, [])
      } catch {
        if (signature !== checkedSignature) return
        settleOn(picked, [])
      }
    })().finally(() => {
      if (pending === request) pending = null
    })
    // A save pressed right after the pick waits on THIS request, not on nothing.
    pending = request
    return request
  }

  // The « changer » beside a picked street: back to the list, nothing refetched.
  const reopenHouses = () => {
    if (houses.value.length < 2) return
    house.value = null
    placeCandidates.value = []
    placeAnswer.value = null
    state.value = 'choose'
  }

  // « Créer / Enregistrer sans localisation »: the person takes the decision in
  // words; the save goes through without coordinates (red badge after).
  const acceptUnlocated = () => {
    if (state.value === 'not_found') state.value = 'unlocated'
  }
  const reopenUnlocated = () => {
    if (state.value === 'unlocated') state.value = 'not_found'
  }

  const answerPlace = (answer: PlaceAnswer) => {
    placeAnswer.value = answer
    state.value = 'found'
  }
  // Re-raise the SAME question with the same candidates; no refetch.
  const reopenPlace = () => {
    if (placeCandidates.value.length === 0) return
    placeAnswer.value = null
    state.value = 'candidate'
  }

  // The edit forms open on an address that was saved already: it owes no new
  // answer until it changes.
  const settleCurrent = () => {
    const draft = read()
    checkedSignature = draft ? signatureOf(draft) : ''
    settled.value = !!draft && !!draft.civicNumber.trim()
    state.value = 'idle'
    wipe()
  }

  // « changer » on a pill: the text is up for re-entry, so it owes an answer
  // again — the next blur or the save asks, and the answer folds the pill back.
  const unsettle = () => {
    checkedSignature = ''
    settled.value = false
    state.value = 'idle'
    wipe()
  }

  const blocksSave = computed(() => STOPPING.has(state.value))
  const chosenLocationId = computed(() =>
    placeAnswer.value?.use === 'existing' ? placeAnswer.value.candidate.id : null,
  )
  const coordinates = computed(() =>
    house.value ? { latitude: house.value.latitude, longitude: house.value.longitude } : null,
  )

  // The hosts' §55 wipe — a closed conversation starts clean.
  const reset = () => {
    clearTimeout(postalTimer)
    state.value = 'idle'
    settled.value = false
    wipe()
    checkedSignature = ''
    pending = null
  }

  return {
    state, settled, houses, house, placeCandidates, placeAnswer,
    touch, onPostalInput, check, ensureAnswered,
    pickHouse, reopenHouses, acceptUnlocated, reopenUnlocated, answerPlace, reopenPlace,
    settleCurrent, unsettle, blocksSave, chosenLocationId, coordinates, reset,
  }
}

export type AddressLookup = ReturnType<typeof useAddressLookup>
